import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import modules from './Ranges.module.css';
import { Alert, Box, Button, Stack } from '@mui/material';
import { Add, AddCircleOutline, Close, DeleteOutline, Edit } from '@mui/icons-material';
import { DataGrid, GridActionsCellItem, gridClasses, GridRowModes, useGridApiRef } from '@mui/x-data-grid';
import { v4 } from 'uuid';
import { DialogActionItem } from 'components/grid';
import { useDispatch, useSelector } from 'react-redux';
import { editRangesModesModel, setEditedRanges, editEditedRanges } from 'store/profile/profileSlice';
import { editedToData } from 'utils/datagrid';
import StrippedDataGrid from 'components/styled';

const Toolbar = ({apiRef}) => {

  const dispatch = useDispatch();

  const addRange = useCallback(() => {
    const model = {id: '-' + v4(), start: 0, end: 0};
    //apiRef.current.updateRows([model]);
    //editEditedItems(model);
    dispatch(editEditedRanges(model));
    dispatch(editRangesModesModel({[model.id]: {mode: GridRowModes.Edit, fieldToFocus: 'start'}}));
  }, [apiRef]);

  return (
    <Alert severity="info" sx={{width: "100%", justifyContent: "center", alignItems: "center", ['& .MuiAlert-message']: {width: '100%'}}}>
      <Stack width='100%' direction='row' spacing={1} sx={{alignItems: 'center', justifyContent: 'flex-end'}}>
        <Box sx={{whiteSpace:'normal', textAlign: 'center', flexGrow: 1}}>
          Диапазоны заводских номеров касс, которым будет назначен этот профиль в качестве первоначального профиля по-умолчанию 
        </Box>
        <Button variant='contained' startIcon={<Add/> } onClick={addRange}
          sx={{width: '36px', minWidth:'36px', height: '36px', borderRadius: '18px',
            ['& .MuiButton-icon']: {margin: 0}, ['& .MuiSvgIcon-root']: {fontSize: '24px'}}}></Button>
      </Stack>
    </Alert>
  );
};

const rowClassName = (row, indexRelativeToCurrentPage) => {
  let result = indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
  row.disabled && (result += ' disabled');
  return result;
};

const Ranges = forwardRef(({items, flush, flushData}, ref) => {
  
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const { rangesModesModel } = useSelector(state => state.profile);
   
  useEffect( () => {
      if(flush) {     
          //dispatch(resetRangesModesModel());
          flushData('ranges', editedToData(apiRef.current.state.editRows));
      }
    }, [flush, apiRef]);

  useLayoutEffect( () => {
    apiRef.current.setRows(items);
  }, [items, rangesModesModel, apiRef]);   

  useLayoutEffect( () => { 
    return () => {
      //setEditedItems(editedToData(apiRef.current.state.editRows));
      dispatch(setEditedRanges(editedToData(apiRef.current.state.editRows)));
    }
  }, [apiRef]);

  
  const remove = useCallback((id) => () => { 
    //editEditedItems({id, deleted: true});
    dispatch(editEditedRanges({id, deleted: true}));
  }, []);

  const editClick = useCallback((id) => () => {
    dispatch(editRangesModesModel({[id]: {mode: GridRowModes.Edit, fieldToFocus: 'start'}}));
  }, []);

  const cancelClick = useCallback((id) => () => {
    dispatch(editRangesModesModel({[id]: {mode: GridRowModes.View, ignoreModifications: true}}));
  }, []);

  const columns = useMemo(
    () => [
      {field: 'start', editable: true, headerName: 'Начало диапазона', headerAlign: 'center', flex: 1, align: 'center'},
      {field: 'end', editable: true, headerName: 'Конец диапазона', flex: 1, headerAlign: 'center', align: 'center'},
      {field: 'actions', headerName: '', type: 'actions', align: 'center', 
        getActions: ({id, row}) => {
          const isEditMode = rangesModesModel[id]?.mode === GridRowModes.Edit;
          if(isEditMode) return [
            <GridActionsCellItem
              icon={<Close sx={{color: 'rgba(0,0,0, 0.54)'}}/>}
              label="Отменить"
              onClick={cancelClick(id)}
              color="inherit"
            />
          ];
          return [
            <GridActionsCellItem
              icon={<Edit />}
              label="Изменить"
              onClick={editClick(id)}
            />,
            <DialogActionItem
             icon={ <DeleteOutline />}
             label="delete"
             handler={remove(id)}
             title='Удалить профиль с касс'
            >
              <span>Удалить с касс диапазона <b>{row.start} - {row.end}</b> профиль по-умолчанию?</span>                
            </DialogActionItem>
          ];
        }
      }
    ]
  );

  return (
    <div ref={ref}>
      
      <StrippedDataGrid
        apiRef={apiRef}
        editMode="row"
        slots={{toolbar: Toolbar}}
        slotProps={{
          toolbar: {apiRef}
        }}
        rowModesModel={rangesModesModel}
        columns={columns}
        rows={items}
        localeText={{noRowsLabel: "Профиль пока не назначен ни одной кассе"}}
        getRowClassName={({row, indexRelativeToCurrentPage}) => rowClassName(row, indexRelativeToCurrentPage)}
        disableColumnResize
        disableColumnMenu
        hideFooter
        sx={{
          [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
          },
          [`& .${gridClasses.columnHeader}, & .MuiInputBase-root`]: {
            overflowX: 'unset'
          },
          [`& .${gridClasses.cell}.${gridClasses["cell--editing"]}:focus, & .${gridClasses.cell}.${gridClasses["cell--editing"]}:focus-within`]: {
            outline: 'none'
          },
          [`& .disabled.${gridClasses.row}, & .disabled.${gridClasses.row}:hover`]: {
            backgroundColor: 'rgba(0, 0, 0, 0.33)'
          }
        }}
      />
    </div>
  );
});

export default Ranges;